"use client"

import { useState, useEffect } from "react"
import {
  FaSearch,
  FaFilter,
  FaDownload,
  FaEye,
  FaFileInvoice,
  FaCheckCircle,
  FaTimesCircle,
  FaClipboardList,
  FaMoneyBillWave,
} from "react-icons/fa"
import Card from "../admincomponents/Card"
import Table from "../admincomponents/Table"

const BillingPayments = () => {
  const [activeTab, setActiveTab] = useState("requests")
  const [showInvoiceModal, setShowInvoiceModal] = useState(false)
  const [currentInvoice, setCurrentInvoice] = useState(null)
  const [paymentRequests, setPaymentRequests] = useState([])
  const [loadingAction, setLoadingAction] = useState(null); // stores which row is being processed (e.g., "accept-3" or "reject-2")

  // --- Fetch enrollment requests ---
  useEffect(() => {
    const fetchEnrollmentRequests = async () => {
      try {
        // Get the JWT token from localStorage
        const token = localStorage.getItem('jwttoken'); // <-- IMPORTANT: Replace 'jwtToken' with your actual key

        const response = await fetch("https://kaasbackend.vetrinahealthcare.com/lms/enrollment-requests/", {
          headers: {
            'Authorization': `Bearer ${token}` // Add the Authorization header
          }
        })

        if (!response.ok) {
          throw new Error('Failed to fetch enrollment requests');
        }

        const data = await response.json()
        const transformedData = data.map((request) => ({
          id: request.request_id,
          user: request.full_name,
          email: request.email,
          course: request.course_title,
          amount: request.course_price,
          date: request.join_date ? new Date(request.join_date).toLocaleDateString() : 'N/A',
          status: request.status === "A" ? "Accepted" : request.status === "R" ? "Rejected" : "Pending",
        }))
        setPaymentRequests(transformedData)
      } catch (error) {
        console.error("Error fetching enrollment requests:", error)
      }
    }

    fetchEnrollmentRequests()
  }, [])


  // --- Sample payments (completed transactions) ---
  const paymentsData = [
    {
      id: "INV-001",
      user: "John Doe",
      email: "john@example.com",
      amount: 49.99,
      date: "2023-03-15",
      status: "Paid",
      method: "Credit Card",
      items: [{ name: "Introduction to React", price: 49.99 }],
    },
    {
      id: "INV-002",
      user: "Jane Smith",
      email: "jane@example.com",
      amount: 69.99,
      date: "2023-03-10",
      status: "Paid",
      method: "PayPal",
      items: [{ name: "Advanced Python Programming", price: 69.99 }],
    },
    {
      id: "INV-003",
      user: "Bob Johnson",
      email: "bob@example.com",
      amount: 39.99,
      date: "2023-03-05",
      status: "Pending",
      method: "Bank Transfer",
      items: [{ name: "Digital Marketing Fundamentals", price: 39.99 }],
    },
  ]

  // --- Accept / Reject request actions ---
  const handleAccept = async (id) => {
    try {
      setLoadingAction(`accept-${id}`); // start loader
      // Get the JWT token from localStorage
      const token = localStorage.getItem('jwttoken'); // <-- IMPORTANT: Replace 'jwtToken' with your actual key

      await fetch(`https://kaasbackend.vetrinahealthcare.com/lms/enrollment-requests/${id}/approve/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`, // Add the Authorization header
          'Content-Type': 'application/json'
        },
      });
      setPaymentRequests((prev) =>
        prev.map((req) => (req.id === id ? { ...req, status: "Accepted" } : req))
      )
    } catch (error) {
      console.error("Error accepting request:", error);
    }
    finally {
      setLoadingAction(null); // stop loader
    }
  }

  const handleReject = async (id) => {
    try {
      setLoadingAction(`reject-${id}`); // start loader
      // Get the JWT token from localStorage
      const token = localStorage.getItem('jwttoken'); // <-- IMPORTANT: Replace 'jwtToken' with your actual key

      await fetch(`https://kaasbackend.vetrinahealthcare.com/lms/enrollment-requests/${id}/reject/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`, // Add the Authorization header
          'Content-Type': 'application/json'
        },
      });
      setPaymentRequests((prev) =>
        prev.map((req) => (req.id === id ? { ...req, status: "Rejected" } : req))
      )
    } catch (error) {
      console.error("Error rejecting request:", error);
    }
    finally {
      setLoadingAction(null); // stop loader
    }
  }


  // --- Handle invoice modal ---
  const handleViewInvoice = (invoice) => {
    setCurrentInvoice(invoice)
    setShowInvoiceModal(true)
  }

  // --- Table Columns ---
  const requestColumns = [
    { key: "id", label: "Request ID", sortable: true },
    { key: "user", label: "Student", sortable: true },
    { key: "email", label: "Email", sortable: true },
    { key: "course", label: "Course", sortable: true },
    {
      key: "amount",
      label: "Amount",
      sortable: true,
      render: (row) => <div>₹{row.amount.toFixed(2)}</div>,
    },
    { key: "date", label: "Date", sortable: true },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (row) => (
        <span
          style={{
            padding: "3px 8px",
            borderRadius: "12px",
            fontSize: "12px",
            backgroundColor:
              row.status === "Accepted"
                ? "#e6f7e6"
                : row.status === "Rejected"
                  ? "#ffe6e6"
                  : "#fff8e6",
            color:
              row.status === "Accepted"
                ? "#4caf50"
                : row.status === "Rejected"
                  ? "#f44336"
                  : "#ff9800",
          }}
        >
          {row.status}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <div style={{ display: "flex", gap: "10px" }}>
          {/* Accept Button */}
          <button
            onClick={() => handleAccept(row.id)}
            disabled={row.status !== "Pending" || loadingAction === `accept-${row.id}` || loadingAction === `reject-${row.id}`}
            style={{
              background: "none",
              border: "none",
              color: row.status === "Pending" ? "#4caf50" : "#ccc",
              cursor: row.status === "Pending" ? "pointer" : "not-allowed",
              fontSize: "18px",
              position: "relative",
            }}
          >
            {loadingAction === `accept-${row.id}` ? (
              <div
                className="spinner-border spinner-border-sm text-success"
                role="status"
                style={{ width: "18px", height: "18px" }}
              />
            ) : (
              <FaCheckCircle />
            )}
          </button>

          {/* Reject Button */}
          <button
            onClick={() => handleReject(row.id)}
            disabled={row.status !== "Pending" || loadingAction === `accept-${row.id}` || loadingAction === `reject-${row.id}`}
            style={{
              background: "none",
              border: "none",
              color: row.status === "Pending" ? "#f44336" : "#ccc",
              cursor: row.status === "Pending" ? "pointer" : "not-allowed",
              fontSize: "18px",
              position: "relative",
            }}
          >
            {loadingAction === `reject-${row.id}` ? (
              <div
                className="spinner-border spinner-border-sm text-danger"
                role="status"
                style={{ width: "18px", height: "18px" }}
              />
            ) : (
              <FaTimesCircle />
            )}
          </button>
        </div>
      ),
    }

  ]

  const paymentColumns = [
    { key: "id", label: "Invoice ID", sortable: true },
    { key: "user", label: "User", sortable: true },
    {
      key: "amount",
      label: "Amount",
      sortable: true,
      render: (row) => <div>${row.amount.toFixed(2)}</div>,
    },
    { key: "date", label: "Date", sortable: true },
    { key: "method", label: "Payment Method", sortable: true },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (row) => (
        <span
          style={{
            padding: "3px 8px",
            borderRadius: "12px",
            fontSize: "12px",
            backgroundColor:
              row.status === "Paid"
                ? "#e6f7e6"
                : row.status === "Pending"
                  ? "#fff8e6"
                  : "#ffe6e6",
            color:
              row.status === "Paid"
                ? "#4caf50"
                : row.status === "Pending"
                  ? "#ff9800"
                  : "#f44336",
          }}
        >
          {row.status}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            onClick={() => handleViewInvoice(row)}
            style={{
              background: "none",
              border: "none",
              color: "#7366ff",
              cursor: "pointer",
              fontSize: "16px",
            }}
          >
            <FaEye />
          </button>
          <button
            style={{
              background: "none",
              border: "none",
              color: "#7366ff",
              cursor: "pointer",
              fontSize: "16px",
            }}
          >
            <FaDownload />
          </button>
        </div>
      ),
    },
  ]

  return (
    <div>
      <h1 style={{ marginBottom: "20px", color: "#333" }}>Billing & Payments</h1>

      {/* --- Tabs --- */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
        <button
          onClick={() => setActiveTab("requests")}
          style={{
            padding: "10px 20px",
            borderRadius: "8px",
            border: "none",
            cursor: "pointer",
            background:
              activeTab === "requests"
                ? "linear-gradient(135deg, #ff5baf, #7366ff)"
                : "#f1f1f1",
            color: activeTab === "requests" ? "white" : "#333",
            fontWeight: "bold",
          }}
        >
          <FaClipboardList /> Requests
        </button>
        <button
          onClick={() => setActiveTab("payments")}
          style={{
            padding: "10px 20px",
            borderRadius: "8px",
            border: "none",
            cursor: "pointer",
            background:
              activeTab === "payments"
                ? "linear-gradient(135deg, #ff5baf, #7366ff)"
                : "#f1f1f1",
            color: activeTab === "payments" ? "white" : "#333",
            fontWeight: "bold",
          }}
        >
          <FaMoneyBillWave /> Payments
        </button>
      </div>

      {/* --- Requests Table --- */}
      {activeTab === "requests" && (
        <Card title="Student Payment Requests">
          <Table columns={requestColumns} data={paymentRequests} pagination={true} itemsPerPage={5} />
        </Card>
      )}

      {/* --- Payments Table --- */}
      {activeTab === "payments" && (
        <Card title="Payment History">
          <Table columns={paymentColumns} data={paymentsData} pagination={true} itemsPerPage={5} />
        </Card>
      )}

      {/* --- Invoice Modal --- */}
      {showInvoiceModal && currentInvoice && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "10px",
              padding: "20px",
              width: "600px",
              maxWidth: "90%",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h2 style={{ margin: 0 }}>Invoice {currentInvoice.id}</h2>
              <FaFileInvoice style={{ fontSize: "24px", color: "#7366ff" }} />
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "20px" }}>
              <div>
                <div style={{ fontWeight: "bold", marginBottom: "5px" }}>Billed To:</div>
                <div>{currentInvoice.user}</div>
                <div>{currentInvoice.email}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontWeight: "bold", marginBottom: "5px" }}>Invoice Date:</div>
                <div>{currentInvoice.date}</div>
                <div
                  style={{
                    marginTop: "10px",
                    padding: "3px 8px",
                    borderRadius: "12px",
                    fontSize: "12px",
                    display: "inline-block",
                    backgroundColor:
                      currentInvoice.status === "Paid"
                        ? "#e6f7e6"
                        : currentInvoice.status === "Pending"
                          ? "#fff8e6"
                          : "#ffe6e6",
                    color:
                      currentInvoice.status === "Paid"
                        ? "#4caf50"
                        : currentInvoice.status === "Pending"
                          ? "#ff9800"
                          : "#f44336",
                  }}
                >
                  {currentInvoice.status}
                </div>
              </div>
            </div>

            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "20px" }}>
              <thead>
                <tr style={{ background: "#f9f9f9" }}>
                  <th style={{ padding: "10px", textAlign: "left", borderBottom: "1px solid #ddd" }}>Item</th>
                  <th style={{ padding: "10px", textAlign: "right", borderBottom: "1px solid #ddd" }}>Price</th>
                </tr>
              </thead>
              <tbody>
                {currentInvoice.items.map((item, index) => (
                  <tr key={index}>
                    <td style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>{item.name}</td>
                    <td style={{ padding: "10px", textAlign: "right", borderBottom: "1px solid #ddd" }}>
                      ${item.price.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ padding: "10px", fontWeight: "bold", textAlign: "right" }}>Total:</td>
                  <td style={{ padding: "10px", fontWeight: "bold", textAlign: "right" }}>
                    ${currentInvoice.amount.toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </table>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button
                onClick={() => setShowInvoiceModal(false)}
                style={{
                  padding: "8px 15px",
                  background: "#f5f5f5",
                  border: "1px solid #ddd",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
              <button
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  padding: "8px 15px",
                  background: "linear-gradient(135deg, #ff5baf, #7366ff)",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
              >
                <FaDownload /> Download
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BillingPayments